import { Observable, throwError } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { Injectable, NgZone } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';

@Injectable()
export class HttpService { 
        
    constructor(private _http: HttpClient, private ngZone: NgZone) {
    }
    
    tokenAPI: string;

    /**
     * Ensures observable emissions run inside NgZone so Angular change detection triggers.
     * This fixes Angular 22's provideHttpClient with functional interceptors
     * delivering responses outside the zone.
     */
    private insideZone<T>(obs: Observable<T>): Observable<T> {
        return new Observable<T>(subscriber => {
            return obs.subscribe({
                next: (value) => this.ngZone.run(() => subscriber.next(value)),
                error: (err) => this.ngZone.run(() => subscriber.error(err)),
                complete: () => this.ngZone.run(() => subscriber.complete())
            });
        });
    }

    httpGet(url: string, userRoles?: any): Observable<any> {
        const rolesValue = Array.isArray(userRoles) ? userRoles.join(',') : (userRoles || '');
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + this.tokenAPI,
            'UserRoles': rolesValue
        });
        return this.insideZone(this._http.get<any>(url, { headers })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    httpGetFile(url: string, contentType: string, userRoles?: any): Observable<Blob> {
        const rolesValue = Array.isArray(userRoles) ? userRoles.join(',') : (userRoles || '');
        const headers = new HttpHeaders({
            'Content-Type': contentType,
            'Authorization': 'Bearer ' + this.tokenAPI,
            'UserRoles': rolesValue
        });
        return this.insideZone(this._http.get(url, { headers, responseType: 'blob' })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    httpPost(url: string, body: any, userRoles?: any, accessToken: string = null, responseType: 'json' | 'text' = 'json'): Observable<any> {
        const token = accessToken || this.tokenAPI;
        const rolesValue = Array.isArray(userRoles) ? userRoles.join(',') : (userRoles || '');
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + token,
            'UserRoles': rolesValue
        });
        if (responseType === 'text') {
            return this.insideZone(this._http.post(url, body, { headers, responseType: 'text' })).pipe(
                catchError(this.errorCustomHandler)
            );
        }
        return this.insideZone(this._http.post<any>(url, body, { headers })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    httpPut(url: string, body: any): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + this.tokenAPI
        });
        return this.insideZone(this._http.put<any>(url, body, { headers })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    httpDelete(url: string): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + this.tokenAPI
        });
        return this.insideZone(this._http.delete<any>(url, { headers })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    httpPatch(url: string, body: any): Observable<any> {
        const headers = new HttpHeaders({
            'Content-Type': 'application/json',
            'Authorization': 'Bearer ' + this.tokenAPI
        });
        return this.insideZone(this._http.patch<any>(url, body, { headers })).pipe(
            catchError(this.errorCustomHandler)
        );
    }

    errorCustomHandler(error: any): Observable<any> {
        if (error.status === 401 && error.url && error.url.endsWith('GenerateToken')) {
            return throwError(() => error || 'Server error');
        }
        if (error.status === 401) {
            console.log('Redirect to Home: Error 401 unauthorized');
            window.location.href = window.location.origin;
        }
        return throwError(() => error || 'Server error');
    }

    setToken(id_token: string): void {
        this.tokenAPI = id_token;
    }
}
